import { useCallback, useMemo } from 'react';
import { useApi } from '@core/http/useApi';
import { applicationPaths } from '@core/http/applicationPaths';
import { isEmpty } from 'lodash';
import {
  formatDateForApi,
  getPathPayload,
  isErrorCodeResult,
  isNotNullOrEmptyString,
  isValidationErrorResult,
} from '@hwm/ui-lib';
import { t } from '@lingui/macro';
import {
  ChannelTypeUnitsAndPercentageModel,
  ErrataInfoModel,
  FilterUnitsPostModel,
  GraphDataModel,
  GraphFiltersModel,
  GraphFiltersPostModel,
  GraphModel,
} from '@shared/models';
import { useGetByIds } from '@core/store/hooks/entityHooks';
import { siteReducer } from '@core/store/entity/sites/SitesReducer';
import { EntityReducers } from '@core/store/EntityReducer';
import { useAppDispatch } from '@core/store/store';
import { graphSitesUiSlice } from './store/GraphSitesUIReducer';
import { useNotifications } from '@shared/hooks/useNotifications';

export const useGraphDataSource = () => {
  const dispatch = useAppDispatch();
  const { handleError } = useNotifications();

  const graphSites = useGetByIds(
    siteReducer.entityAdapter,
    EntityReducers.sites,
    (state) => state['graph-sites-ui'].loadedIds
  );

  const setGraphSites = useCallback(
    (ids: string[]) => {
      dispatch(graphSitesUiSlice.actions.setIds({ ids }));
    },
    [dispatch]
  );

  const [getChannelsConfig] = useApi<
    null,
    null,
    getPathPayload<typeof applicationPaths.graph.getChannelsConfiguration>
  >(
    useCallback(({ siteId, deviceId }) => applicationPaths.graph.getChannelsConfiguration({ siteId, deviceId }), []),
    'post',
    { omit: true }
  );

  const getMultipleChannelsConfig = useCallback(
    async (deviceDataForConfigRequest: ErrataInfoModel[]) => {
      const promises = deviceDataForConfigRequest?.map(async ({ siteId, deviceId }) =>
        getChannelsConfig({ siteId: siteId.toString(), deviceId: deviceId.toString() })
      );

      await Promise.all(promises);
    },
    [getChannelsConfig]
  );

  const [getGraphData, graphData, graphLoading, graphError] = useApi<GraphModel, GraphFiltersPostModel>(
    applicationPaths.graph.graphData,
    'post'
  );

  const graphDataFiltered = useMemo(() => {
    if (!graphData || isEmpty(graphData)) {
      return { boundaries: null, errata: null, data: null };
    }

    const filteredData: GraphDataModel[] =
      graphData.data?.map((data) => {
        return {
          ...data,
          channelData: data.channelData.filter((chData) => Boolean(chData.data) && !isEmpty(chData.data)),
        };
      }) ?? null;

    return { ...graphData, data: filteredData };
  }, [graphData]);

  const handleGetGraphData = useCallback(
    (userSiteIds: string[], filterState: GraphFiltersModel) => {
      if (Boolean(userSiteIds && !isEmpty(userSiteIds))) {
        const units: FilterUnitsPostModel = filterState.units.reduce((acc, { type, unit, interval }) => {
          return {
            ...acc,
            [type]: { name: unit, interval },
          } as FilterUnitsPostModel;
        }, {});

        const { showAlarmBoundariesAsPercentage, ...filterStateData } = filterState ?? {};

        const body = {
          ...filterStateData,
          units,
          from: formatDateForApi(filterState.from),
          to: formatDateForApi(filterState.to),
        } as GraphFiltersPostModel;

        const params = new URLSearchParams();
        userSiteIds.forEach((id) => {
          params.append('siteIds', id);
        });

        getGraphData({
          body,
          additionalOptions: { params },
        }).catch((err) => {
          if (!isValidationErrorResult(err) && !isErrorCodeResult(err) && !isNotNullOrEmptyString(err)) {
            handleError(err, t`Graph`, t`There was a problem while trying to get graph data`);
          } else {
            handleError(err, t`Graph`);
          }
        });
      }
    },
    [getGraphData, handleError]
  );

  const [getGraphUnitsAndPercentage, graphUnitsAndPercentage, unitsLoading, unitsError] =
    useApi<ChannelTypeUnitsAndPercentageModel>(applicationPaths.graph.graphUnits, 'get');

  const handleGetGraphUnits = useCallback(
    (userSiteIds: string[], cb?: (data: ChannelTypeUnitsAndPercentageModel) => void) => {
      if (userSiteIds && !isEmpty(userSiteIds)) {
        const params = new URLSearchParams();
        userSiteIds?.forEach((id) => {
          params.append('siteIds', id);
        });
        getGraphUnitsAndPercentage({
          additionalOptions: { params },
        })
          .then((data) => cb?.(data))
          .catch((err) => {
            handleError(err, t`Graph`, t`There was a problem while trying to get graph units`);
          });
      }
    },
    [getGraphUnitsAndPercentage, handleError]
  );

  return {
    getMultipleChannelsConfig,
    graphSites,
    setGraphSites,
    graphData: graphDataFiltered,
    getGraphData: handleGetGraphData,
    graphUnitsAndPercentage,
    getGraphUnits: handleGetGraphUnits,
    loading: graphLoading,
    unitsLoading,
    error: graphError || unitsError,
  };
};
